/**
 * Git Trigger — Enterprise git push / PR verification
 *
 * Installs git hooks that evaluate changed files against the PDLSS policy
 * before allowing pushes to remote repositories.
 *
 * Two enforcement modes (admin-configurable):
 *   - block: non-zero exit prevents the push
 *   - warn:  push proceeds but warning is printed
 */

import { existsSync, mkdirSync, writeFileSync, readFileSync, chmodSync, unlinkSync } from 'fs';
import { join } from 'path';
import { execSync } from 'child_process';
import type { AstraSyncGateway } from '../gateway/gateway';
import { mapToolToPurpose, extractTarget } from '../adapter-interface/purpose-mapping';
import type { PDLSSContext, VerificationDecision } from '../gateway/types';

// -----------------------------------------------------------------------
// Configuration
// -----------------------------------------------------------------------

export interface GitTriggerConfig {
  /** Block the push or just warn (default: 'warn') */
  enforcement: 'block' | 'warn';
  /** Which git hooks to install (default: ['pre-push']) */
  hooks: ('pre-push' | 'pre-commit')[];
  /** Only enforce on these branches (default: all) */
  protectedBranches?: string[];
  /** Custom message for blocked pushes */
  blockMessage?: string;
}

const DEFAULT_CONFIG: GitTriggerConfig = {
  enforcement: 'warn',
  hooks: ['pre-push'],
};

// -----------------------------------------------------------------------
// Hook script content
// -----------------------------------------------------------------------

const HOOK_MARKER = '# AstraSync Local Guard — managed hook';

function makeHookScript(hookType: 'pre-push' | 'pre-commit'): string {
  return `#!/bin/sh
${HOOK_MARKER}
# Runs AstraSync verification on changed files before ${hookType === 'pre-push' ? 'push' : 'commit'}.
# Installed by: astrasync guard install-hooks
# To remove: delete this file or run astrasync guard uninstall-hooks

npx astrasync guard check --trigger=${hookType} "$@"
exit $?
`;
}

// -----------------------------------------------------------------------
// Hook installer
// -----------------------------------------------------------------------

/**
 * Install git hooks into the repository.
 * Returns the paths of installed hooks.
 */
export function installGitHooks(
  repoRoot: string,
  config: Partial<GitTriggerConfig> = {},
): { installed: string[]; skipped: string[] } {
  const merged = { ...DEFAULT_CONFIG, ...config };
  const hooksDir = join(repoRoot, '.git', 'hooks');
  const installed: string[] = [];
  const skipped: string[] = [];

  if (!existsSync(join(repoRoot, '.git'))) {
    throw new Error(`Not a git repository: ${repoRoot}`);
  }

  if (!existsSync(hooksDir)) {
    mkdirSync(hooksDir, { recursive: true });
  }

  for (const hookType of merged.hooks) {
    const hookPath = join(hooksDir, hookType);

    // Don't overwrite non-AstraSync hooks
    if (existsSync(hookPath)) {
      const content = readFileSync(hookPath, 'utf-8');
      if (!content.includes(HOOK_MARKER)) {
        skipped.push(hookPath);
        continue;
      }
    }

    writeFileSync(hookPath, makeHookScript(hookType), 'utf-8');
    chmodSync(hookPath, 0o755);
    installed.push(hookPath);
  }

  return { installed, skipped };
}

/**
 * Remove AstraSync-managed git hooks.
 */
export function uninstallGitHooks(
  repoRoot: string,
  hooks: ('pre-push' | 'pre-commit')[] = ['pre-push'],
): string[] {
  const hooksDir = join(repoRoot, '.git', 'hooks');
  const removed: string[] = [];

  for (const hookType of hooks) {
    const hookPath = join(hooksDir, hookType);
    if (existsSync(hookPath)) {
      const content = readFileSync(hookPath, 'utf-8');
      if (content.includes(HOOK_MARKER)) {
        unlinkSync(hookPath);
        removed.push(hookPath);
      }
    }
  }

  return removed;
}

// -----------------------------------------------------------------------
// Evaluation logic
// -----------------------------------------------------------------------

/**
 * Get list of changed files for the current trigger context.
 */
export function getChangedFiles(trigger: 'pre-push' | 'pre-commit', repoRoot: string): string[] {
  try {
    let output: string;
    if (trigger === 'pre-push') {
      // Files changed between HEAD and remote tracking branch
      output = execSync('git diff --name-only @{push}.. 2>/dev/null || git diff --name-only HEAD~1', {
        cwd: repoRoot,
        encoding: 'utf-8',
      });
    } else {
      // Files staged for commit
      output = execSync('git diff --cached --name-only', {
        cwd: repoRoot,
        encoding: 'utf-8',
      });
    }
    return output.split('\n').filter(f => f.trim().length > 0);
  } catch {
    return [];
  }
}

/**
 * Map a file change to a PDLSS context for evaluation.
 */
export function fileChangeToPDLSSContext(filePath: string): PDLSSContext {
  const purpose = mapToolToPurpose('file_write');
  const target = extractTarget('file_write', { path: filePath });

  return {
    purpose,
    action: 'file_write',
    target,
    dataAccess: ['write'],
    resourceType: 'file',
  };
}

/**
 * Evaluate changed files against the gateway policy.
 * Returns a summary of allow/deny/review decisions.
 */
export async function evaluateChangedFiles(
  gateway: AstraSyncGateway,
  files: string[],
  config: Partial<GitTriggerConfig> = {},
): Promise<GitCheckResult> {
  const merged = { ...DEFAULT_CONFIG, ...config };
  const results: { file: string; decision: VerificationDecision }[] = [];
  let blocked = 0;
  let warned = 0;
  let allowed = 0;

  for (const file of files) {
    const context = fileChangeToPDLSSContext(file);
    const decision = await gateway.evaluate(context);

    results.push({ file, decision });

    if (decision.recommendation === 'DENY') {
      blocked++;
    } else if (decision.recommendation === 'MANUAL_REVIEW') {
      warned++;
    } else {
      allowed++;
    }
  }

  const shouldBlock = merged.enforcement === 'block' && (blocked > 0);

  return {
    allowed,
    blocked,
    warned,
    total: files.length,
    shouldBlock,
    enforcement: merged.enforcement,
    results,
    blockMessage: shouldBlock
      ? (merged.blockMessage || `AstraSync blocked push: ${blocked} file(s) denied by policy`)
      : undefined,
  };
}

export interface GitCheckResult {
  allowed: number;
  blocked: number;
  warned: number;
  total: number;
  shouldBlock: boolean;
  enforcement: 'block' | 'warn';
  results: { file: string; decision: VerificationDecision }[];
  blockMessage?: string;
}

/**
 * Check if the current branch is protected.
 */
export function isProtectedBranch(config: Partial<GitTriggerConfig> = {}): boolean {
  if (!config.protectedBranches || config.protectedBranches.length === 0) {
    return true; // All branches protected by default
  }

  try {
    const branch = execSync('git rev-parse --abbrev-ref HEAD', { encoding: 'utf-8' }).trim();
    return config.protectedBranches.some(pattern => {
      if (pattern === branch) return true;
      if (pattern.endsWith('*') && branch.startsWith(pattern.slice(0, -1))) return true;
      return false;
    });
  } catch {
    return true; // Can't determine branch, assume protected
  }
}
